import { postService } from 'core/services'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import * as interfaces from '../../static/interfaces'
import { Button, InputText, InputTextArea } from '../atoms'
import { Form } from '../molecules'
import Typography from 'components/atoms/_layouts/Typography'

declare var window: interfaces.IWindow
interface IErrors {
  [key: string]: string | null
}

interface IProps {
  post: interfaces.ICustomPost
  user: any
  cancelPolicy?: string
}

interface IFormProps {
  postDetail: interfaces.IPostDetail
  handleFormSubmit: (values: any) => Promise<void>
  setLocalValue: React.Dispatch<any>
  cancelPolicy?: string
}

const FIELDS = {
  name: 'name',
  overview: 'overview',
  highlight: 'highlight',
  itinerary: 'itinerary',
  included: 'included',
  not_included: 'not_included',
  remarks: 'remarks',
  cancel_policy: 'cancel_policy',
  ogp_description: 'ogp_description',
  food: 'food',
  drinks: 'drinks',
  tickets: 'tickets',
  transportation: 'transportation',
  equipment: 'equipment',
}

const INCLUDED_ITEM_FIELDS: Array<{
  key: interfaces.IIncludedCategory
  type: interfaces.IIncludedCategory
}> = [
  { key: 'food', type: 'food' },
  { key: 'drinks', type: 'drinks' },
  { key: 'tickets', type: 'tickets' },
  { key: 'transportation', type: 'transportation' },
  { key: 'equipment', type: 'equipment' },
]

const normalizePostDetail = (postDetail: interfaces.IPostDetail): interfaces.IPostDetail => {
  const includedItems =
    postDetail.included_items && postDetail.included_items.length > 0
      ? postDetail.included_items
      : INCLUDED_ITEM_FIELDS.reduce<interfaces.IIncludedItem[]>((items, item) => {
          const description = postDetail[item.key]
          if (description) {
            items.push({ type: item.type, description })
          }
          return items
        }, [])

  return {
    ...postDetail,
    included_items: includedItems,
  }
}

const normalizePost = (post: interfaces.ICustomPost): interfaces.ICustomPost => ({
  ...post,
  post_details: post.post_details.map(normalizePostDetail),
})

const PostDetailInfo: React.FC<IProps> = props => {
  const [post, setPost] = React.useState(normalizePost(props.post))
  const [localValue, setLocalValue] = React.useState(null)
  const [saving, setSaving] = React.useState(false)
  const targetDetail = post.post_details.find(detail => detail.display_language?.code === 'en') || post.post_details[0]
  const updatePostDetail = async params => {
    if (post) {
      params.id = post.id
    }

    if (!props.user.admin) {
      delete params.pickup
    }

    const { postDetail, flush } = await postService.updatePostDetail({
      ...params,
      id: targetDetail.id,
    })

    setPost(
      normalizePost({
        ...post,
        post_details: post.post_details.map(detail => {
          if (detail.id === postDetail.id) {
            return postDetail
          }
          return detail
        }),
      })
    )
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    return flush.type
  }

  const handleFormSubmit = async values => {
    setSaving(true)
    await updatePostDetail(values)
    setSaving(false)
  }

  return (
    <>
      <h2 className="Editor_Title">{I18n.t('post.input_detail_info')}</h2>
      <div style={{ padding: 16, height: 16 }}>
        {saving && <Typography type="subtitle">{I18n.t('generic.saving')}</Typography>}
      </div>
      <PostDetailInfoForm
        key={`form-${targetDetail.id}`}
        postDetail={targetDetail}
        handleFormSubmit={handleFormSubmit}
        setLocalValue={setLocalValue}
        cancelPolicy={props.cancelPolicy}
      />
    </>
  )
}

const PostDetailInfoForm: React.FC<IFormProps> = ({
  postDetail,
  handleFormSubmit,
  setLocalValue,
  cancelPolicy,
}) => {
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false)
  const [errors, setErrors] = React.useState<IErrors>({})

  const handleUpdateForm = React.useCallback((updatedErrors, updatedIsSubmitEnabled, values) => {
    setErrors(updatedErrors)
    setIsSubmitEnabled(updatedIsSubmitEnabled)
    if (values) {
      setLocalValue(values)
    }
  }, [])

  const submit = async (_, values: any) => {
    await handleFormSubmit(values)
  }
  return (
    <Form fields={FIELDS} handleUpdateForm={handleUpdateForm} handleSubmit={submit}>
      <ul>
        <FormItem>
          <InputText
            required={true}
            name="name"
            label={I18n.t('post.title')}
            placeholder={I18n.t('post.title')}
            defaultValue={postDetail.name}
            error={errors.name}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="overview"
            label={I18n.t('post.overview')}
            placeholder={I18n.t('post.overview')}
            defaultValue={postDetail.overview}
            error={errors.overview}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="highlight"
            label={I18n.t('post.highlight')}
            placeholder={I18n.t('post.highlight')}
            defaultValue={postDetail.highlight}
            error={errors.highlight}
          />
        </FormItem>
        <FormItem>
          <Typography type="body" color="subtext">
            過去互換用、下記詳細がない場合に表示
          </Typography>
          <InputTextArea
            name="included"
            label={I18n.t('post.included')}
            placeholder={I18n.t('post.included')}
            defaultValue={postDetail.included}
            error={errors.included}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="food"
            label={I18n.t('post.included_food')}
            placeholder={I18n.t('post.included_food')}
            defaultValue={postDetail.food}
            error={errors.food}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="drinks"
            label={I18n.t('post.included_drinks')}
            placeholder={I18n.t('post.included_drinks')}
            defaultValue={postDetail.drinks}
            error={errors.drinks}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="tickets"
            label={I18n.t('post.included_tickets')}
            placeholder={I18n.t('post.included_tickets')}
            defaultValue={postDetail.tickets}
            error={errors.tickets}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="transportation"
            label={I18n.t('post.included_transportation')}
            placeholder={I18n.t('post.included_transportation')}
            defaultValue={postDetail.transportation}
            error={errors.transportation}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="equipment"
            label={I18n.t('post.included_equipment')}
            placeholder={I18n.t('post.included_equipment')}
            defaultValue={postDetail.equipment}
            error={errors.equipment}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="itinerary"
            label={I18n.t('post.itinerary')}
            placeholder={I18n.t('post.itinerary')}
            defaultValue={postDetail.itinerary}
            error={errors.itinerary}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="remarks"
            label={I18n.t('post.remarks')}
            placeholder={I18n.t('post.remarks')}
            defaultValue={postDetail.remarks}
            error={errors.remarks}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="not_included"
            label={I18n.t('post.not_included')}
            placeholder={I18n.t('post.not_included')}
            defaultValue={postDetail.not_included}
            error={errors.not_included}
          />
        </FormItem>
        <FormItem>
          <InputTextArea
            name="cancel_policy"
            label={I18n.t('post.cancel_policy')}
            placeholder={cancelPolicy}
            defaultValue=""
            error={errors.cancel_policy}
            disabled={true}
          />
        </FormItem>

        <FormItem>
          <InputTextArea
            name="ogp_description"
            label={I18n.t('post.ogp_description')}
            placeholder={I18n.t('post.ogp_description')}
            defaultValue={postDetail.ogp_description}
            error={errors.ogp_description}
          />
        </FormItem>
      </ul>
      <div className="Editor_Footer">
        <Button primary={true} disabled={!isSubmitEnabled}>
          {I18n.t('generic.update')}
        </Button>
      </div>
    </Form>
  )
}

const FormItem = styled.div`
  ${({ hide }) => hide && 'display: none'}

  & + & {
    margin-top: 16px;
  }
`

export default PostDetailInfo
